import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { CefrLevel, CoverageResponse } from "@/types/dialog";
import { useBrowseStore } from "@/features/browse";
import { usePreferencesStore } from "@/features/preferences";
import { getLanguageName } from "@/lib/language-names";

const LEVELS: CefrLevel[] = ["A1", "A2", "B1", "B2", "C1", "C2"];

export function PreferencesPage() {
  const coverage = useBrowseStore((s) => s.coverage);
  const loadCoverage = useBrowseStore((s) => s.loadCoverage);
  const preferences = usePreferencesStore((s) => s.preferences);
  const prefsLoaded = usePreferencesStore((s) => s.loaded);
  const loadPreferences = usePreferencesStore((s) => s.loadPreferences);
  const addLanguage = usePreferencesStore((s) => s.addLanguage);
  const removeLanguage = usePreferencesStore((s) => s.removeLanguage);
  const updateLevel = usePreferencesStore((s) => s.updateLevel);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!coverage) loadCoverage();
  }, [coverage, loadCoverage]);

  useEffect(() => {
    if (!prefsLoaded) loadPreferences();
  }, [prefsLoaded, loadPreferences]);

  const selectedCodes = new Set(preferences.map((p) => p.language));

  const allLanguages = coverage
    ? buildLanguageList(coverage).filter(
        (l) => !search || l.name.toLowerCase().includes(search.toLowerCase()),
      )
    : [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Language Settings</h1>
        <Link
          to="/"
          className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
        >
          Back to Feed
        </Link>
      </div>

      {preferences.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3">
            Your Languages
          </h2>
          <div className="space-y-2">
            {preferences.map((pref) => {
              const name = getLanguageName(pref.language);
              return (
                <div
                  key={pref.language}
                  className="flex items-center gap-3 bg-th-surface-dim border border-th-border rounded-lg p-3"
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-white font-medium text-sm">{name}</span>
                  </div>
                  <div className="flex gap-1">
                    {LEVELS.map((lvl) => (
                      <button
                        key={lvl}
                        onClick={() => updateLevel(pref.language, lvl)}
                        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                          pref.level === lvl
                            ? "bg-indigo-600 text-white"
                            : "bg-slate-800 text-slate-400 hover:text-slate-200"
                        }`}
                      >
                        {lvl}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => removeLanguage(pref.language)}
                    className="text-slate-600 hover:text-red-400 transition-colors text-lg px-1"
                  >
                    &times;
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3">
          Add Languages
        </h2>
        <input
          type="text"
          placeholder="Search languages..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-2.5 text-sm text-slate-200 placeholder-slate-500 mb-4 outline-none focus:border-indigo-500 transition-colors"
        />
        <div className="grid gap-2 grid-cols-2 sm:grid-cols-3">
          {allLanguages.map((lang) => {
            const isSelected = selectedCodes.has(lang.code);
            return (
              <button
                key={lang.code}
                onClick={() => {
                  if (isSelected) removeLanguage(lang.code);
                  else addLanguage(lang.code);
                }}
                className={`text-left px-3 py-2.5 rounded-lg text-sm transition-colors border ${
                  isSelected
                    ? "bg-indigo-600/20 border-indigo-500/40 text-indigo-300"
                    : "bg-slate-800/60 border-slate-700/40 text-slate-300 hover:border-slate-600"
                }`}
              >
                <div className="font-medium truncate">{lang.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{lang.count} dialogs</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function buildLanguageList(coverage: CoverageResponse) {
  return Object.entries(coverage.by_language)
    .filter(([, v]) => v.published > 0)
    .map(([code, v]) => {
      const name = getLanguageName(code);
      const isKnown = name !== code;
      return { code, name, count: v.published, tier: isKnown ? 0 : 1 };
    })
    .sort((a, b) => a.tier - b.tier || a.name.localeCompare(b.name));
}
